import {
  addDaysToLocalDate,
  compareLocalDates,
  daysBetweenLocalDates,
  type RamadanLocalizedText,
  type RamadanSetupRecord,
} from "./ramadan.js";

export const ramadanIftarDayStatuses = ["draft", "open", "closed"] as const;
export type RamadanIftarDayStatus = (typeof ramadanIftarDayStatuses)[number];

export const ramadanIftarWeekdays = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;
export type RamadanIftarWeekday = (typeof ramadanIftarWeekdays)[number];
export type RamadanIftarCapacityByWeekday = Record<RamadanIftarWeekday, number>;

export const ramadanIftarDedicationTypes = ["general", "inHonorOf", "inMemoryOf"] as const;
export type RamadanIftarDedicationType = (typeof ramadanIftarDedicationTypes)[number];

export type RamadanIftarRecognitionPolicy = {
  anonymousPublicRecognitionAllowed: boolean;
  publicRecognitionAllowed: boolean;
  sponsorDisplayNameAllowed: boolean;
};

export type RamadanIftarDedicationPolicy = {
  allowedTypes: RamadanIftarDedicationType[];
  enabled: boolean;
  maxLength: number;
  publicDisplayAllowed: boolean;
};

export type RamadanIftarSponsorshipOrganizationFeeLine = {
  active: boolean;
  amountMinor: number;
  displayOrder: number;
  feeLineId: string;
  label: RamadanLocalizedText;
};

export type RamadanIftarSponsorshipDefaultsRecord = {
  capacityByWeekday?: RamadanIftarCapacityByWeekday;
  configId: "defaults";
  createdAt: unknown;
  createdBy: string;
  currency: "usd";
  dedication: RamadanIftarDedicationPolicy;
  defaultPricePerSpotMinor: number;
  fridayCapacitySpots: number;
  organizationFeeLines?: RamadanIftarSponsorshipOrganizationFeeLine[];
  organizationId: string;
  programEnabled: boolean;
  publicNotes: RamadanLocalizedText | null;
  ramadanId: string;
  recognition: RamadanIftarRecognitionPolicy;
  saturdayCapacitySpots: number;
  source: "experience_thirty_three";
  sponsorshipClosesDayOffset: number;
  sponsorshipClosesLocalTime: string;
  sponsorshipOpensAt: string | null;
  standardCapacitySpots: number;
  updatedAt: unknown;
  updatedBy: string;
};

export type RamadanIftarDayRecord = {
  capacitySpots: number;
  confirmedSpots: number;
  createdAt: unknown;
  createdBy: string;
  currency: "usd";
  dedication: RamadanIftarDedicationPolicy;
  gregorianDate: string;
  iftarDayId: string;
  organizationFeeLines?: RamadanIftarSponsorshipOrganizationFeeLine[];
  organizationId: string;
  pricePerSpotMinor: number;
  publicNotes: RamadanLocalizedText | null;
  ramadanDay: number;
  ramadanId: string;
  recognition: RamadanIftarRecognitionPolicy;
  reservedSpots: number;
  source: "experience_thirty_three";
  sponsorshipClosesAt: string;
  sponsorshipEnabled: boolean;
  sponsorshipOpensAt: string | null;
  status: RamadanIftarDayStatus;
  timezone: string;
  updatedAt: unknown;
  updatedBy: string;
  weekday: RamadanIftarWeekday;
};

export type RamadanIftarDayGenerationInput = {
  actorUid: string;
  defaults: RamadanIftarSponsorshipDefaultsRecord;
  setup: Pick<
    RamadanSetupRecord,
    "endsOn" | "organizationId" | "ramadanId" | "startsOn" | "timezone"
  >;
  timestamp: unknown;
};

const safeIdentifierPattern = /^[A-Za-z0-9][A-Za-z0-9_-]{1,119}$/u;
const localDatePattern = /^\d{4}-\d{2}-\d{2}$/u;
const localTimePattern = /^([01]\d|2[0-3]):[0-5]\d$/u;
const localDateTimePattern = /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d$/u;
const maximumCapacitySpots = 1000;
const maximumPricePerSpotMinor = 10_000_000;

export function buildRamadanIftarDayId(gregorianDate: string): string {
  return `iftar-${gregorianDate}`;
}

export function isRamadanIftarDayStatus(value: unknown): value is RamadanIftarDayStatus {
  return (
    typeof value === "string" && ramadanIftarDayStatuses.includes(value as RamadanIftarDayStatus)
  );
}

export function isRamadanIftarWeekday(value: unknown): value is RamadanIftarWeekday {
  return typeof value === "string" && ramadanIftarWeekdays.includes(value as RamadanIftarWeekday);
}

export function validateRamadanIftarDefaultsRecord(
  defaults: RamadanIftarSponsorshipDefaultsRecord,
  expectedOrganizationId?: string,
  expectedRamadanId?: string,
): string[] {
  const errors: string[] = [];

  if (defaults.configId !== "defaults") {
    errors.push("Iftar sponsorship defaults config ID must be defaults.");
  }
  if (!isSafeIdentifier(defaults.organizationId)) {
    errors.push("Iftar sponsorship defaults organization ID must be safe.");
  }
  if (expectedOrganizationId !== undefined && defaults.organizationId !== expectedOrganizationId) {
    errors.push("Iftar sponsorship defaults organization ID must match its path.");
  }
  if (!isSafeIdentifier(defaults.ramadanId)) {
    errors.push("Iftar sponsorship defaults Ramadan ID must be safe.");
  }
  if (expectedRamadanId !== undefined && defaults.ramadanId !== expectedRamadanId) {
    errors.push("Iftar sponsorship defaults Ramadan ID must match its path.");
  }
  if (defaults.source !== "experience_thirty_three") {
    errors.push("Iftar sponsorship defaults source must be EXP-033.");
  }
  if (!isCapacity(defaults.standardCapacitySpots)) {
    errors.push("Standard Iftar capacity must be a non-negative safe integer.");
  }
  if (!isCapacity(defaults.fridayCapacitySpots)) {
    errors.push("Friday Iftar capacity must be a non-negative safe integer.");
  }
  if (!isCapacity(defaults.saturdayCapacitySpots)) {
    errors.push("Saturday Iftar capacity must be a non-negative safe integer.");
  }
  if (!isCapacityByWeekday(defaults.capacityByWeekday)) {
    errors.push(
      "Iftar sponsorship weekday capacity must use canonical weekdays and safe capacities.",
    );
  }
  if (!isPrice(defaults.defaultPricePerSpotMinor)) {
    errors.push("Default Iftar price must be a non-negative integer minor-unit amount.");
  }
  if (defaults.currency !== "usd") {
    errors.push("Iftar sponsorship currency must be usd.");
  }
  if (defaults.programEnabled) {
    const hasAnyCapacity = ramadanIftarWeekdays.some(
      (weekday) => capacityForWeekday(defaults, weekday) > 0,
    );
    if (!hasAnyCapacity) {
      errors.push("Enabled Iftar sponsorship defaults need at least one positive capacity.");
    }
    if (defaults.defaultPricePerSpotMinor <= 0) {
      errors.push("Enabled Iftar sponsorship defaults need a positive price.");
    }
  }
  if (
    !Number.isInteger(defaults.sponsorshipClosesDayOffset) ||
    defaults.sponsorshipClosesDayOffset < -60 ||
    defaults.sponsorshipClosesDayOffset > 0
  ) {
    errors.push("Iftar sponsorship close day offset must be between -60 and 0.");
  }
  if (!isLocalTime(defaults.sponsorshipClosesLocalTime)) {
    errors.push("Iftar sponsorship close time must be HH:mm.");
  }
  if (defaults.sponsorshipOpensAt !== null && !isLocalDateTime(defaults.sponsorshipOpensAt)) {
    errors.push("Iftar sponsorship open datetime must be YYYY-MM-DDTHH:mm when present.");
  }
  if (!isRecognitionPolicy(defaults.recognition)) {
    errors.push("Iftar sponsorship recognition policy must be valid.");
  }
  if (!isDedicationPolicy(defaults.dedication)) {
    errors.push("Iftar sponsorship dedication policy must be valid.");
  }
  if (!isOrganizationFeeLines(defaults.organizationFeeLines ?? [])) {
    errors.push("Iftar sponsorship organization fee lines must be valid.");
  }
  if (!isOptionalLocalizedText(defaults.publicNotes, 240)) {
    errors.push("Iftar sponsorship public notes must be localized text when present.");
  }

  return errors;
}

export function validateRamadanIftarDayRecord(
  day: RamadanIftarDayRecord,
  expected?: {
    organizationId?: string;
    ramadanId?: string;
    setup?: Pick<
      RamadanSetupRecord,
      "endsOn" | "organizationId" | "ramadanId" | "startsOn" | "timezone"
    >;
  },
): string[] {
  const errors: string[] = [];

  if (!isSafeIdentifier(day.iftarDayId)) {
    errors.push("Iftar Day ID must be a safe stable identifier.");
  }
  if (!isSafeIdentifier(day.organizationId)) {
    errors.push("Iftar Day organization ID must be safe.");
  }
  if (expected?.organizationId !== undefined && day.organizationId !== expected.organizationId) {
    errors.push("Iftar Day organization ID must match its path.");
  }
  if (!isSafeIdentifier(day.ramadanId)) {
    errors.push("Iftar Day Ramadan ID must be safe.");
  }
  if (expected?.ramadanId !== undefined && day.ramadanId !== expected.ramadanId) {
    errors.push("Iftar Day Ramadan ID must match its path.");
  }
  if (!isLocalDate(day.gregorianDate)) {
    errors.push("Iftar Day date must be YYYY-MM-DD.");
  } else if (day.iftarDayId !== buildRamadanIftarDayId(day.gregorianDate)) {
    errors.push("Iftar Day ID must match its Gregorian date.");
  }
  if (!Number.isInteger(day.ramadanDay) || day.ramadanDay < 1 || day.ramadanDay > 31) {
    errors.push("Iftar Day Ramadan day must be 1 through 31.");
  }
  if (!isRamadanIftarWeekday(day.weekday)) {
    errors.push("Iftar Day weekday must be canonical.");
  } else if (
    isLocalDate(day.gregorianDate) &&
    day.weekday !== weekdayForLocalDate(day.gregorianDate)
  ) {
    errors.push("Iftar Day weekday must match its Gregorian date.");
  }
  if (!isRamadanIftarDayStatus(day.status)) {
    errors.push("Iftar Day status must be canonical.");
  }
  if (!isCapacity(day.capacitySpots)) {
    errors.push("Iftar Day capacity must be a non-negative safe integer.");
  }
  if (!isCapacity(day.reservedSpots)) {
    errors.push("Iftar Day reserved spots must be a non-negative safe integer.");
  }
  if (!isCapacity(day.confirmedSpots)) {
    errors.push("Iftar Day confirmed spots must be a non-negative safe integer.");
  }
  if (
    isCapacity(day.capacitySpots) &&
    isCapacity(day.reservedSpots) &&
    isCapacity(day.confirmedSpots) &&
    day.reservedSpots + day.confirmedSpots > day.capacitySpots
  ) {
    errors.push("Iftar Day reserved plus confirmed spots cannot exceed capacity.");
  }
  if (!isPrice(day.pricePerSpotMinor)) {
    errors.push("Iftar Day price must be a non-negative integer minor-unit amount.");
  }
  if (day.currency !== "usd") {
    errors.push("Iftar Day currency must be usd.");
  }
  if (day.sponsorshipEnabled === false && day.status === "open") {
    errors.push("Disabled Iftar Days cannot be open.");
  }
  if (day.sponsorshipEnabled && day.status === "open") {
    if (day.capacitySpots <= 0) {
      errors.push("Open Iftar Days need positive capacity.");
    }
    if (day.pricePerSpotMinor <= 0) {
      errors.push("Open Iftar Days need a positive price.");
    }
  }
  if (!isLocalDateTime(day.sponsorshipClosesAt)) {
    errors.push("Iftar Day sponsorship close datetime must be YYYY-MM-DDTHH:mm.");
  }
  if (day.sponsorshipOpensAt !== null && !isLocalDateTime(day.sponsorshipOpensAt)) {
    errors.push("Iftar Day sponsorship open datetime must be YYYY-MM-DDTHH:mm when present.");
  }
  if (!isSupportedTimeZone(day.timezone)) {
    errors.push("Iftar Day timezone must be a supported IANA timezone.");
  }
  if (!isRecognitionPolicy(day.recognition)) {
    errors.push("Iftar Day recognition policy must be valid.");
  }
  if (!isDedicationPolicy(day.dedication)) {
    errors.push("Iftar Day dedication policy must be valid.");
  }
  if (!isOrganizationFeeLines(day.organizationFeeLines ?? [])) {
    errors.push("Iftar Day organization fee lines must be valid.");
  }
  if (!isOptionalLocalizedText(day.publicNotes, 240)) {
    errors.push("Iftar Day public notes must be localized text when present.");
  }
  if (day.source !== "experience_thirty_three") {
    errors.push("Iftar Day source must be EXP-033.");
  }

  const setup = expected?.setup;
  if (setup) {
    if (day.organizationId !== setup.organizationId || day.ramadanId !== setup.ramadanId) {
      errors.push("Iftar Day must belong to the same canonical Ramadan setup.");
    }
    if (day.timezone !== setup.timezone) {
      errors.push("Iftar Day timezone must match the canonical Ramadan setup timezone.");
    }
    if (!isDateInsideRamadanSetup(day.gregorianDate, setup)) {
      errors.push("Iftar Day date must be inside canonical Ramadan boundaries.");
    } else {
      const expectedDay = daysBetweenLocalDates(setup.startsOn, day.gregorianDate) + 1;
      if (day.ramadanDay !== expectedDay) {
        errors.push("Iftar Day Ramadan day must derive from canonical Ramadan start date.");
      }
    }
  }

  return errors;
}

export function buildRamadanIftarDaysFromDefaults(
  input: RamadanIftarDayGenerationInput,
): RamadanIftarDayRecord[] {
  const count = daysBetweenLocalDates(input.setup.startsOn, input.setup.endsOn) + 1;
  return Array.from({ length: count }, (_, index) => {
    const gregorianDate = addDaysToLocalDate(input.setup.startsOn, index);
    return buildRamadanIftarDayFromDefaults({
      ...input,
      gregorianDate,
    });
  });
}

export function buildRamadanIftarDayFromDefaults(
  input: RamadanIftarDayGenerationInput & {
    gregorianDate: string;
  },
): RamadanIftarDayRecord {
  const weekday = weekdayForLocalDate(input.gregorianDate);
  const capacitySpots = capacityForWeekday(input.defaults, weekday);
  const sponsorshipEnabled = input.defaults.programEnabled && capacitySpots > 0;

  return {
    capacitySpots,
    createdAt: input.timestamp,
    createdBy: input.actorUid,
    currency: input.defaults.currency,
    dedication: input.defaults.dedication,
    gregorianDate: input.gregorianDate,
    iftarDayId: buildRamadanIftarDayId(input.gregorianDate),
    organizationFeeLines: [...(input.defaults.organizationFeeLines ?? [])],
    organizationId: input.setup.organizationId,
    pricePerSpotMinor: input.defaults.defaultPricePerSpotMinor,
    publicNotes: input.defaults.publicNotes,
    ramadanDay: daysBetweenLocalDates(input.setup.startsOn, input.gregorianDate) + 1,
    ramadanId: input.setup.ramadanId,
    recognition: input.defaults.recognition,
    reservedSpots: 0,
    source: "experience_thirty_three",
    sponsorshipClosesAt: buildRamadanIftarSponsorshipClosesAt({
      closesDayOffset: input.defaults.sponsorshipClosesDayOffset,
      closesLocalTime: input.defaults.sponsorshipClosesLocalTime,
      gregorianDate: input.gregorianDate,
    }),
    sponsorshipEnabled,
    sponsorshipOpensAt: input.defaults.sponsorshipOpensAt,
    status: sponsorshipEnabled ? "open" : "closed",
    timezone: input.setup.timezone,
    updatedAt: input.timestamp,
    updatedBy: input.actorUid,
    weekday,
    confirmedSpots: 0,
  };
}

export function activeRamadanIftarOrganizationFeeTotalMinor(
  feeLines: readonly RamadanIftarSponsorshipOrganizationFeeLine[] | undefined,
): number {
  return (feeLines ?? [])
    .filter((line) => line.active)
    .reduce((total, line) => total + line.amountMinor, 0);
}

export function capacityForWeekday(
  defaults: Pick<
    RamadanIftarSponsorshipDefaultsRecord,
    "capacityByWeekday" | "fridayCapacitySpots" | "saturdayCapacitySpots" | "standardCapacitySpots"
  >,
  weekday: RamadanIftarWeekday,
): number {
  const explicitCapacity = defaults.capacityByWeekday?.[weekday];
  if (isCapacity(explicitCapacity)) {
    return explicitCapacity;
  }
  if (weekday === "friday") {
    return defaults.fridayCapacitySpots;
  }
  if (weekday === "saturday") {
    return defaults.saturdayCapacitySpots;
  }
  return defaults.standardCapacitySpots;
}

export function buildRamadanIftarSponsorshipClosesAt(input: {
  closesDayOffset: number;
  closesLocalTime: string;
  gregorianDate: string;
}): string {
  const closesOn = addDaysToLocalDate(input.gregorianDate, input.closesDayOffset);
  return `${closesOn}T${input.closesLocalTime}`;
}

export function weekdayForLocalDate(gregorianDate: string): RamadanIftarWeekday {
  const date = localDateToUtcDate(gregorianDate);
  const weekday = ramadanIftarWeekdays[date.getUTCDay()];
  if (!weekday) {
    throw new Error("Unable to derive weekday for local date.");
  }
  return weekday;
}

export function isDateInsideRamadanSetup(
  gregorianDate: string,
  setup: Pick<RamadanSetupRecord, "endsOn" | "startsOn">,
): boolean {
  return (
    isLocalDate(gregorianDate) &&
    compareLocalDates(gregorianDate, setup.startsOn) >= 0 &&
    compareLocalDates(gregorianDate, setup.endsOn) <= 0
  );
}

function isSafeIdentifier(value: unknown): value is string {
  return typeof value === "string" && safeIdentifierPattern.test(value);
}

function isLocalDate(value: unknown): value is string {
  if (typeof value !== "string" || !localDatePattern.test(value)) {
    return false;
  }
  return localDateToUtcDate(value).toISOString().slice(0, 10) === value;
}

function isLocalTime(value: unknown): value is string {
  return typeof value === "string" && localTimePattern.test(value);
}

function isLocalDateTime(value: unknown): value is string {
  if (typeof value !== "string" || !localDateTimePattern.test(value)) {
    return false;
  }
  const [date, time] = value.split("T");
  return isLocalDate(date) && isLocalTime(time);
}

function localDateToUtcDate(value: string): Date {
  return new Date(`${value}T00:00:00.000Z`);
}

function isSupportedTimeZone(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

function isCapacity(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= maximumCapacitySpots
  );
}

function isCapacityByWeekday(value: unknown): value is RamadanIftarCapacityByWeekday | undefined {
  if (value === undefined) {
    return true;
  }
  if (!isRecord(value)) {
    return false;
  }
  return ramadanIftarWeekdays.every((weekday) => isCapacity(value[weekday]));
}

function isPrice(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= maximumPricePerSpotMinor
  );
}

function isRecognitionPolicy(value: unknown): value is RamadanIftarRecognitionPolicy {
  if (!isRecord(value)) {
    return false;
  }
  const publicRecognitionAllowed = value.publicRecognitionAllowed;
  const sponsorDisplayNameAllowed = value.sponsorDisplayNameAllowed;
  const anonymousPublicRecognitionAllowed = value.anonymousPublicRecognitionAllowed;
  if (
    typeof publicRecognitionAllowed !== "boolean" ||
    typeof sponsorDisplayNameAllowed !== "boolean" ||
    typeof anonymousPublicRecognitionAllowed !== "boolean"
  ) {
    return false;
  }
  return (
    publicRecognitionAllowed || (!sponsorDisplayNameAllowed && !anonymousPublicRecognitionAllowed)
  );
}

function isDedicationPolicy(value: unknown): value is RamadanIftarDedicationPolicy {
  if (!isRecord(value)) {
    return false;
  }
  const enabled = value.enabled;
  const allowedTypes = value.allowedTypes;
  const maxLength = value.maxLength;
  const publicDisplayAllowed = value.publicDisplayAllowed;
  if (typeof enabled !== "boolean" || !Array.isArray(allowedTypes)) {
    return false;
  }
  if (typeof publicDisplayAllowed !== "boolean" || typeof maxLength !== "number") {
    return false;
  }
  if (!Number.isInteger(maxLength) || maxLength < 0 || maxLength > 280) {
    return false;
  }
  const uniqueTypes = new Set(allowedTypes);
  if (uniqueTypes.size !== allowedTypes.length) {
    return false;
  }
  if (
    !allowedTypes.every(
      (type) =>
        typeof type === "string" &&
        ramadanIftarDedicationTypes.includes(type as RamadanIftarDedicationType),
    )
  ) {
    return false;
  }
  if (enabled) {
    return allowedTypes.length > 0 && maxLength > 0;
  }
  return true;
}

function isOrganizationFeeLines(
  value: unknown,
): value is RamadanIftarSponsorshipOrganizationFeeLine[] {
  if (!Array.isArray(value) || value.length > 8) {
    return false;
  }
  const ids = new Set<string>();
  for (const line of value) {
    if (!isRecord(line)) {
      return false;
    }
    if (!isSafeIdentifier(line.feeLineId) || ids.has(line.feeLineId)) {
      return false;
    }
    ids.add(line.feeLineId);
    if (typeof line.active !== "boolean") {
      return false;
    }
    if (!isPrice(line.amountMinor)) {
      return false;
    }
    const displayOrder = line.displayOrder;
    if (
      typeof displayOrder !== "number" ||
      !Number.isInteger(displayOrder) ||
      displayOrder < 0 ||
      displayOrder > 1000
    ) {
      return false;
    }
    if (!isLocalizedText(line.label, 80)) {
      return false;
    }
  }
  return true;
}

function isOptionalLocalizedText(value: unknown, maxLength: number): boolean {
  return value === null || value === undefined || isLocalizedText(value, maxLength);
}

function isLocalizedText(value: unknown, maxLength: number): value is RamadanLocalizedText {
  if (!isRecord(value) || typeof value.en !== "string") {
    return false;
  }
  const english = value.en.trim();
  if (english.length === 0 || english.length > maxLength) {
    return false;
  }
  return (
    value.ar === null ||
    value.ar === undefined ||
    (typeof value.ar === "string" &&
      value.ar.trim().length > 0 &&
      value.ar.trim().length <= maxLength)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
